import {UI, type PartialOptions} from "../../../stem-core/ui/UIBase";
import {longWeekdays, StemDate} from "../../../stem-core/time/Date";
import {Select, TextInput, TimeInput} from "../../../stem-core/ui/input/Input";
import {BlinkInputField} from "../../common/Input";
import {findFirstFreeVersion, toArray, unwrapArray} from "../../../stem-core/base/Utils";
import {AudienceNewsletterFrequency, MerchantAudience} from "../../../client/state/merchant/MerchantAudienceStore";
import {Switcher} from "../../../stem-core/ui/Switcher";
import {ConfirmationModal} from "../../../blinkpay/ui/ConfirmationModal";
import {LineSeparator} from "../../../blinkpay/ui/LineSeparator";
import {CheckboxInput} from "../../../stem-core/ui/input/checkbox/CheckboxInput.jsx";
import {RadioButtonsInput} from "../../../core/ui/input/radio/RadioButtonsInput.jsx";
import {VisibilityStatus} from "../../../client/state/misc/GenericEnums.js";
import {BlinkSDKInputField, SDKArrayInput} from "../user-experience/components/Primitives.jsx";
import {DropdownInput} from "../../../core/ui/input/dropdown/DropdownInput.jsx";
import {SubscriptionBenefit} from "../../../client/state/SubscriptionBenefitStore.js";
import type {StoreId} from "../../../stem-core/state/State";

interface WeekdayMultiSelectorOptions {
    initialValue?: any;
}

class WeekdayMultiSelector extends UI.Element<WeekdayMultiSelectorOptions> {
    render() {
        const WEEKDAYS = longWeekdays;
        const valuesSelected = new Set(this.options.initialValue);
        return WEEKDAYS.map((name, index) => <span>
            <CheckboxInput ref={"day" + index} initialValue={valuesSelected.has(index)} label={name}/>
        </span>);
    }

    getValue() {
        let result = [];
        for (let index = 0; index < longWeekdays.length; index++) {
            if (this["day" + index].getValue()) {
                result.push(index);
            }
        }
        return result;
    }
}

interface MonthDayMultiSelectorOptions {
    initialValue?: any;
}

class MonthDayMultiSelector extends UI.Element<MonthDayMultiSelectorOptions> {
    declare input: TextInput;

    render() {
        let {initialValue} = this.options;
        return [
            <BlinkInputField label="Days of the month, comma separated (from 1 to 31)">
                <TextInput ref="input" value={initialValue.join(",")} />
            </BlinkInputField>
        ]
    }

    getValue() {
        let value = this.input.getValue();
        let values = value.split(",").map(x => parseInt(x));
        return unwrapArray(values);
    }
}

interface MerchantAudienceInputOptions {
    // Partial because the create flow starts from the seed getDefaultOptions builds, which has no name or
    // visibility yet; editing an existing one passes the whole store object
    audience?: Partial<MerchantAudience>;
}

export class MerchantAudienceInput extends UI.Element<MerchantAudienceInputOptions> {
    // Built once and reused across redraws
    declare monthDaySelector?: any;
    // Built once and reused across redraws
    declare weekdaySelector?: any;
    declare autosubscribeInput: CheckboxInput;
    declare autosubscribeOnBenefitsInput: BlinkSDKInputField;
    declare frequencyInput: Select<AudienceNewsletterFrequency>;
    declare frequencyOffsetsInput: MonthDayMultiSelector;
    declare isPublicInput: CheckboxInput;
    declare nameInput: TextInput;
    declare sendTimeInput: TimeInput;
    declare switcher: Switcher;
    declare visibilityInput: RadioButtonsInput;

    getDefaultOptions(): PartialOptions<MerchantAudienceInput> {
        let defaultSendTime = new StemDate();
        defaultSendTime.setHours(9, 0, 0, 0);
        return {
            audience: {
                isPublic: false,
                autosubscribeNewUsers: false,
                defaultFrequency: AudienceNewsletterFrequency.WEEKLY,
                defaultFrequencyOffsets: [1, 15], // Works for both monthly and weekly
                defaultSendTime,
            }
        }
    }

    getValue() {
        return {
            name: this.nameInput.getValue(),
            visibility: this.visibilityInput.getValue(),
            isPublic: this.isPublicInput.getValue(),
            defaultFrequency: this.frequencyInput.getValue(),
            defaultFrequencyOffsets: this.frequencyOffsetsInput?.getValue() || [],
            defaultSendTime: this.sendTimeInput.getValue(),
            autosubscribeNewUsers: this.autosubscribeInput.getValue(),
            autosubscribeOnBenefitIds: toArray(this.autosubscribeOnBenefitsInput.getValue()),
        }
    }

    getFrequencyOffsetsSelector() {
        let {audience} = this.options;

        if (!this.weekdaySelector) {
            const weekdaySelectorDefault = (audience.defaultFrequency == AudienceNewsletterFrequency.WEEKLY) ?
                        audience.defaultFrequencyOffsets : [1];
            const monthDaySelectorDefault = (audience.defaultFrequency == AudienceNewsletterFrequency.MONTHLY) ?
                        audience.defaultFrequencyOffsets : [1, 15];
            this.weekdaySelector = <WeekdayMultiSelector ref="frequencyOffsetsInput"
                                                         initialValue={weekdaySelectorDefault}
                                                         />;
            this.monthDaySelector = <MonthDayMultiSelector ref="frequencyOffsetsInput"
                                                           initialValue={monthDaySelectorDefault}
                                                           />;
        }

        const selectedFrequency = this.frequencyInput?.getValue() || audience.defaultFrequency;
        if (selectedFrequency == AudienceNewsletterFrequency.WEEKLY) {
            return this.weekdaySelector;
        }
        if (selectedFrequency == AudienceNewsletterFrequency.MONTHLY) {
            return this.monthDaySelector;
        }
        return null;
    }

    render() {
        let {audience} = this.options;

        return [
            <div>
                <BlinkInputField label="Name">
                    <TextInput ref="nameInput" value={audience.name}/>
                </BlinkInputField>

                <BlinkInputField label="Visibility">
                    <RadioButtonsInput
                        values={VisibilityStatus.all()}
                        initialValue={audience.visibility}
                        ref="visibilityInput"
                    />
                </BlinkInputField>
            </div>,
            <div>
                <CheckboxInput ref="isPublicInput" initialValue={audience.isPublic} label={"Users can self-subscribe"}/>
            </div>,
            <div>
                <CheckboxInput
                    ref={"autosubscribeInput"}
                    initialValue={audience.autosubscribeNewUsers}
                    label={"Automatically Add New Users"}
                />
            </div>,
            <div>
                <BlinkSDKInputField
                    ref="autosubscribeOnBenefitsInput"
                    label="Add user on new benefits"
                    style={{margin: "8px 0px"}}
                    optional
                    initialValue={audience.autosubscribeOnBenefitIds.length > 0 ? audience.autosubscribeOnBenefitIds : null}
                    InputClass={SDKArrayInput}
                    inputOptions={{
                        box: false,
                        addButtonLabel: "Add benefit",
                        ElementInputClass: DropdownInput,
                        elementInputOptions: {
                            formatter: (id: StoreId) => SubscriptionBenefit.get(id),
                            options: SubscriptionBenefit.all().map(benefit => benefit.id),
                        }
                    }}/>
            </div>,
            <LineSeparator text="Campaign template options, used when creating new campaigns for this audience."/>,
            <div>
                <BlinkInputField label="Default Frequency">
                    <Select ref="frequencyInput"
                            options={AudienceNewsletterFrequency.all()}
                            initialValue={audience.defaultFrequency}
                            onChange={() => {
                                this.switcher.setActive(this.getFrequencyOffsetsSelector());
                            }}
                    />
                </BlinkInputField>
            </div>,

            <Switcher ref="switcher">
                {this.getFrequencyOffsetsSelector()}
            </Switcher>,

            <BlinkInputField label="Default send time">
                <TimeInput ref="sendTimeInput" value={audience.defaultSendTime}/>
            </BlinkInputField>,
        ]
    }
}


export class MerchantAudienceCreateDialog extends ConfirmationModal {
    declare nameInput: TextInput;

    getDefaultOptions(options?: this["options"]) {
        return {
            ...super.getDefaultOptions(),
            title: "Create audience",
            confirmLabel: "Create & continue editing",
            confirmAction: () => this.resolve({
                name: this.nameInput.getValue(),
            }),
        };
    }

    render() {
        const defaultName = findFirstFreeVersion("New Audience", (name) => MerchantAudience.find(audience => audience.name === name));
        return [
            <BlinkInputField label="Name">
                <TextInput ref="nameInput" value={defaultName} style={{minWidth: 300}}/>
            </BlinkInputField>
        ]
    }
}
